// =====================================================================
// commande-prete/TempsEncodage.jsx — panneau « ⏱ Temps d'encodage » à droite de la page (27-10-2026)
// =====================================================================
// Affiché seulement avec « Administrer les commandes prêtes » (la page décide ; le serveur refuse sinon).
// Le temps MESURÉ va du scan au 1er mail et compte les pauses. Une commande de plus d'une heure (oubliée, laissée en
// attente) est une ANOMALIE : son temps facturable est à corriger — le serveur propose un temps d'après l'historique
// (temps de l'opérateur par palette/colis, sinon celui de l'équipe), l'admin accepte ou met un autre temps avec un motif.
// La facturation se fait COMMANDE par COMMANDE (liste filtrable par client) ; la vue par opérateur ne sert qu'au rythme.
// =====================================================================

import { useEffect, useRef, useState } from 'react';
import { readyOrders, errMsg } from '../../services/api.js';
import { enregistrerReponse, messageDeBlob } from '../../lib/telechargement.js';
import { dateHeureBxl } from './dates.js';
import {
  PERIODES, DEBUT_MESURE, bornes, erreurPeriode, duree, aujourdhuiBxl, unites, parUnite, auMax,
  texteProposition, erreurCorrection, commandes,
} from './tempsEncodage.js';

const carte = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.9rem' };
const puce = (actif) => ({ border: `1px solid ${actif ? '#5b2333' : '#cbd5e1'}`, background: actif ? '#5b2333' : '#fff', color: actif ? '#fff' : '#334155',
  borderRadius: 6, padding: '4px 8px', fontSize: '0.78rem', cursor: 'pointer' });
const cellN = { textAlign: 'right', whiteSpace: 'nowrap', padding: '6px 4px' };
const ROUGE = '#fde8e1';
const boutonVert = { background: '#166534', color: '#fff', border: 0, borderRadius: 6, padding: '5px 10px', fontWeight: 600, cursor: 'pointer' };
const boutonGris = { background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 10px', cursor: 'pointer' };

export default function TempsEncodage() {
  const [cle, setCle] = useState('tout');
  const [libre, setLibre] = useState({ du: DEBUT_MESURE, au: aujourdhuiBxl() });
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [exportEnCours, setExportEnCours] = useState(false);
  const [client, setClient] = useState(null);
  const [vueOperateur, setVueOperateur] = useState(false);
  const [ouvert, setOuvert] = useState(null);
  const [recharge, setRecharge] = useState(0);
  const [aRevoir, setARevoir] = useState([]);         // commandes déjà corrigées qu'on rouvre (« modifier »)
  const dernierePeriode = useRef('');

  const periode = cle === 'libre' ? libre : bornes(cle);
  const invalide = cle === 'libre' ? erreurPeriode(libre.du, libre.au) : null;

  useEffect(() => {
    // Nouvelle période : on efface d'abord (chiffres et export suivent toujours la période affichée) et on revient à
    // « Tous les clients ». Simple rechargement après une correction : l'écran reste (saisies en cours conservées).
    const cle = `${periode.du}|${periode.au}|${invalide || ''}`;
    // (un message de conflit, lui, reste affiché pendant le rechargement qui suit)
    if (cle !== dernierePeriode.current) { dernierePeriode.current = cle; setData(null); setClient(null); setARevoir([]); setErr(''); }
    if (invalide) { setData(null); return undefined; }
    let actuel = true;
    readyOrders.statsEncodage(periode.du, periode.au)
      .then((r) => { if (actuel) setData(r); })
      .catch((e) => { if (actuel) setErr(errMsg(e)); });
    return () => { actuel = false; };
  }, [periode.du, periode.au, invalide, recharge]);

  const exporter = async () => {
    if (exportEnCours) return;   // pas « disabled » : le garde de focus de la page y verrait un focus tombé
    setExportEnCours(true); setErr('');
    try { enregistrerReponse(await readyOrders.exportEncodage(periode.du, periode.au), 'commandes-pretes-temps-encodage.xlsx'); }
    catch (e) { setErr(await messageDeBlob(e)); }
    finally { setExportEnCours(false); }
  };

  const toutes = commandes(data);
  const clients = [...new Set(toutes.map((d) => d.client || '—'))].sort((a, b) => a.localeCompare(b, 'fr'));
  const clientActif = client && clients.includes(client) ? client : null;
  const liste = commandes(data, clientActif);
  const anomalies = toutes.filter((d) => (d.facturable === null || aRevoir.includes(d.id)) && (vueOperateur || !clientActif || (d.client || '—') === clientActif))
    .sort((x, y) => String(x.fin).localeCompare(String(y.fin)));
  const recharger = (message) => { if (message) setErr(message); setRecharge((n) => n + 1); };
  const fermer = (id) => setARevoir((l) => l.filter((x) => x !== id));
  const apres = (id) => { fermer(id); recharger(); };

  return (
    <aside id="tempsEncodage" style={carte} aria-label="Temps d'encodage">
      <h2 style={{ margin: 0, fontSize: '1.05rem' }}>⏱ Temps d'encodage</h2>
      <p style={{ margin: '4px 0 10px', fontSize: '0.8rem', color: '#64748b' }}>Du scan de la référence au 1<sup>er</sup> mail « prête » envoyé au client.</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {PERIODES.map((p) => <button key={p.cle} type="button" style={puce(cle === p.cle)} onClick={() => setCle(p.cle)}>{p.libelle}</button>)}
        <button type="button" style={puce(cle === 'libre')} onClick={() => setCle('libre')}>📅 Du … au …</button>
      </div>
      {cle === 'libre' && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, fontSize: '0.8rem' }}>
          <label>Du <input type="date" min={DEBUT_MESURE} max={libre.au || undefined} value={libre.du} onChange={(e) => setLibre((v) => ({ ...v, du: e.target.value }))} /></label>
          <label>au <input type="date" min={libre.du || DEBUT_MESURE} max={auMax(libre.du)} value={libre.au} onChange={(e) => setLibre((v) => ({ ...v, au: e.target.value }))} /></label>
        </div>
      )}
      {invalide && <div role="alert" style={{ color: '#a6342b', fontSize: '0.82rem', marginBottom: 8 }}>{invalide}</div>}
      {err && <div role="alert" style={{ color: '#a6342b', fontSize: '0.82rem', marginBottom: 8 }}>{err}</div>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
            {[[data.commandes, 'commandes'], [duree(data.minutesMediane), 'temps médian'], [data.plusDUneHeure, 'anomalies'], [data.aCorriger, 'à corriger']].map(([v, l]) => (
              <div key={l} style={{ background: l === 'à corriger' && v ? ROUGE : '#f8fafc', borderRadius: 8, padding: 8, fontSize: '0.72rem', color: '#64748b' }}>
                <b style={{ display: 'block', fontSize: '1.1rem', color: '#1e293b' }}>{v}</b>{l}
              </div>
            ))}
          </div>

          {anomalies.length > 0 && (
            <section id="anomaliesEncodage" style={{ marginBottom: 12 }}>
              <h3 style={{ margin: '4px 0 6px', fontSize: '0.88rem', color: '#9a3412' }}>⚠ À vérifier avant facturation ({anomalies.length})</h3>
              {anomalies.map((d) => <Anomalie key={d.id} d={d} min={data.minCommandesOperateur} apres={() => apres(d.id)} recharger={recharger} fermer={d.corrige ? () => fermer(d.id) : null} />)}
            </section>
          )}

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '6px 0' }}>
            <h3 style={{ margin: 0, fontSize: '0.88rem', flex: '1 1 auto' }}>{vueOperateur ? 'Par opérateur (rythme)' : 'Par commande (pour la facturation)'}</h3>
            <button type="button" style={{ ...boutonGris, padding: '3px 8px', fontSize: '0.75rem' }} onClick={() => setVueOperateur((v) => !v)}>
              {vueOperateur ? '◂ Vue par commande' : 'Vue par opérateur ▸'}
            </button>
          </div>

          {!vueOperateur && (
            <>
              {clients.length > 1 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                  <button type="button" style={puce(clientActif === null)} onClick={() => setClient(null)}>Tous les clients</button>
                  {clients.map((c) => <button key={c} type="button" style={puce(clientActif === c)} onClick={() => setClient(c)}>{c}</button>)}
                </div>
              )}
              {liste.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Aucune commande envoyée sur cette période.</p>
              ) : (
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead><tr style={{ color: '#64748b', fontSize: '0.7rem', textAlign: 'left' }}>
                      <th style={{ padding: '6px 4px' }}>Commande</th><th style={{ padding: '6px 4px' }}>Client</th><th style={{ padding: '6px 4px' }}>Opérateur</th>
                      <th style={cellN}>Unités</th><th style={cellN}>Mesuré</th><th style={cellN}>Facturable</th>
                    </tr></thead>
                    <tbody>
                      {liste.map((d) => (
                        <tr key={d.id} style={{ borderTop: '1px solid #f1f5f9', background: d.facturable === null ? ROUGE : undefined }}>
                          <td style={{ padding: '6px 4px', overflowWrap: 'anywhere' }}><b>{d.orderRef}</b><div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{dateHeureBxl(d.fin)}</div></td>
                          <td style={{ padding: '6px 4px' }}>{d.client || '—'}</td>
                          <td style={{ padding: '6px 4px' }}>{d.operateur}</td>
                          <td style={cellN}>{unites(d.palettes, d.colis)}</td>
                          <td style={cellN}>{d.corrige || d.long ? <s>{duree(d.minutes)}</s> : duree(d.minutes)}</td>
                          <td style={cellN}>
                            {d.facturable === null
                              ? <span style={{ color: '#9a3412', fontWeight: 700 }}>à corriger</span>
                              : <><b>{duree(d.facturable)}</b>{d.corrige && (
                                  <div title={`${d.corrige.motif} — ${d.corrige.par || ''}`} style={{ color: '#166534', fontSize: '0.68rem' }}>
                                    corrigé · <button type="button" onClick={() => setARevoir((l) => (l.includes(d.id) ? l : [...l, d.id]))} style={{ border: 0, background: 'none', padding: 0, color: '#5b2333', textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}>modifier</button>
                                  </div>
                                )}</>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {vueOperateur && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead><tr style={{ color: '#64748b', fontSize: '0.72rem', textAlign: 'left' }}>
                <th style={{ padding: '6px 4px' }}>Opérateur</th><th style={cellN}>Cmd</th><th style={cellN}>Pal. / colis</th><th style={cellN}>Médian</th><th style={cellN} title="Temps médian par palette ou colis">/ unité</th><th style={cellN}>&gt; 60 min</th>
              </tr></thead>
              <tbody>
                {data.utilisateurs.map((u) => (
                  <Lignes key={u.userId} u={u} ouvert={ouvert === u.userId} basculer={() => setOuvert((o) => (o === u.userId ? null : u.userId))} />
                ))}
              </tbody>
            </table>
          )}

          <button type="button" id="exportEncodage" aria-busy={exportEnCours} disabled={data.commandes === 0} onClick={exporter}
            style={{ marginTop: 12, border: '1px solid #0f5132', color: '#0f5132', background: '#fff', borderRadius: 8, padding: '6px 12px', fontWeight: 600, cursor: 'pointer' }}>
            {exportEnCours ? '…' : '⬇ Exporter Excel'}
          </button>
          <p style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 10, lineHeight: 1.45 }}>
            Le temps mesuré compte les pauses : au-delà de {data.seuilMinutes} min, la commande est une anomalie et son temps facturable est
            à corriger (le temps mesuré reste visible, barré). La proposition vient de l'historique des {data.historiqueJours} derniers jours
            (commandes normales) : le temps de l'opérateur par palette ou colis s'il a au moins {data.minCommandesOperateur} commandes, sinon
            celui de l'équipe. Les corrections envoyées après le 1<sup>er</sup> mail ne comptent pas ; commandes de test exclues.
          </p>
        </>
      )}
    </aside>
  );
}

function Anomalie({ d, apres, recharger, min, fermer }) {
  const [autre, setAutre] = useState(!!fermer);       // rouverte depuis « modifier » : directement la saisie
  const [minutes, setMinutes] = useState(String(d.corrige?.minutes ?? d.proposition?.minutes ?? ''));
  const [motif, setMotif] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState('');
  const saisieInvalide = autre ? erreurCorrection(minutes, motif, d.minutes) : null;

  const enregistrer = async (min, texte) => {
    if (enCours) return;
    setEnCours(true); setMsg('');
    try { await readyOrders.corrigerEncodage(d.id, min, texte); apres(); }
    catch (e) {
      setEnCours(false);
      if (e?.response?.status === 409) recharger(`${d.orderRef} : ${errMsg(e)} — la valeur affichée est désormais celle enregistrée.`);
      else setMsg(errMsg(e));
    }
  };

  return (
    <div style={{ border: '1px solid #f5c2b3', background: '#fff8f5', borderRadius: 8, padding: 10, marginBottom: 8, fontSize: '0.8rem' }}>
      <div><b>{d.orderRef}</b> · {d.operateur} · {unites(d.palettes, d.colis)}{d.client ? ` · ${d.client}` : ''}</div>
      <div style={{ color: '#64748b', fontSize: '0.75rem', margin: '3px 0' }}>Scan {dateHeureBxl(d.debut)} → mail {dateHeureBxl(d.fin)} · mesuré <s>{duree(d.minutes)}</s></div>
      {d.corrige && <div style={{ color: '#166534', fontSize: '0.75rem' }}>Actuel : {duree(d.corrige.minutes)} — « {d.corrige.motif} »{d.corrige.par ? ` · ${d.corrige.par}` : ''}</div>}
      {d.proposition
        ? <div>Proposé : <b>{duree(d.proposition.minutes)}</b> <span style={{ color: '#64748b', fontSize: '0.74rem' }}>({d.palettes + d.colis} unités × {texteProposition(d.proposition, min)})</span></div>
        : <div style={{ color: '#64748b' }}>Pas de proposition (aucune palette ou colis, ou pas encore d'historique) : indiquez le temps.</div>}
      {!autre && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {d.proposition && (
            <button type="button" style={boutonVert} aria-busy={enCours}
              onClick={() => enregistrer(d.proposition.minutes, `Temps proposé accepté (${texteProposition(d.proposition, min)})`)}>
              {enCours ? '…' : `✓ Accepter ${duree(d.proposition.minutes)}`}
            </button>
          )}
          <button type="button" style={boutonGris} onClick={() => setAutre(true)}>✎ Autre temps…</button>
        </div>
      )}
      {autre && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: '0.75rem' }}>Temps facturable (minutes, au plus {d.minutes})
            <input type="number" min={0} max={d.minutes} step={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} style={{ display: 'block', width: 120, marginTop: 2 }} />
          </label>
          <label style={{ fontSize: '0.75rem' }}>Motif (obligatoire)
            <textarea rows={2} maxLength={300} value={motif} onChange={(e) => setMotif(e.target.value)} style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 2 }}
              placeholder="ex. commande laissée ouverte la nuit, mail envoyé le lendemain" />
          </label>
          {saisieInvalide && <div style={{ color: '#9a3412', fontSize: '0.74rem' }}>{saisieInvalide}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" style={{ ...boutonVert, opacity: saisieInvalide ? 0.5 : 1 }} aria-busy={enCours}
              onClick={() => { if (!saisieInvalide) enregistrer(Number(minutes), motif.trim()); }}>{enCours ? '…' : 'Enregistrer'}</button>
            <button type="button" style={boutonGris} onClick={() => { if (fermer) fermer(); else { setAutre(false); setMsg(''); } }}>Annuler</button>
          </div>
        </div>
      )}
      {msg && <div role="alert" style={{ color: '#a6342b', fontSize: '0.75rem', marginTop: 4 }}>{msg}</div>}
    </div>
  );
}

function Lignes({ u, ouvert, basculer }) {
  return (
    <>
      <tr onClick={basculer} style={{ cursor: 'pointer', background: ouvert ? '#f5eef0' : undefined, borderTop: '1px solid #f1f5f9' }}>
        <td style={{ padding: '6px 4px' }}><button type="button" aria-expanded={ouvert} style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit' }}>{ouvert ? '▾' : '▸'} {u.nom}</button></td>
        <td style={cellN}>{u.commandes}</td>
        <td style={cellN}>{unites(u.palettes, u.colis)}</td>
        <td style={{ ...cellN, fontWeight: 700 }}>{duree(u.minutesMediane)}</td>
        <td style={cellN}>{parUnite(u.minutesParUniteMediane)}</td>
        <td style={cellN}>{u.plusDUneHeure ? <span style={{ background: ROUGE, color: '#9a3412', borderRadius: 8, padding: '1px 7px', fontWeight: 700 }}>{u.plusDUneHeure}</span> : '—'}</td>
      </tr>
      {ouvert && u.detail.map((d) => (
        <tr key={d.id} style={{ fontSize: '0.74rem', color: '#475569', background: d.facturable === null ? ROUGE : '#fcfafb' }}>
          <td colSpan={2} style={{ padding: '5px 4px 5px 14px', overflowWrap: 'anywhere' }}>{d.orderRef}{d.client ? ` · ${d.client}` : ''}</td>
          <td style={cellN}>{unites(d.palettes, d.colis)}</td>
          <td colSpan={2} style={{ padding: '5px 4px' }}>{dateHeureBxl(d.debut)} → {dateHeureBxl(d.fin)}</td>
          <td style={cellN}>{d.corrige ? <><s>{duree(d.minutes)}</s><div style={{ color: '#166534' }}>{duree(d.corrige.minutes)} corrigé</div></> : duree(d.minutes)}{!d.corrige && d.minutesParUnite !== null ? <div style={{ color: '#94a3b8' }}>{parUnite(d.minutesParUnite)}</div> : null}</td>
        </tr>
      ))}
    </>
  );
}
